/**
 * useApplicantApprovalStatus — the admin/director approval-status join
 * (approval-status-ssot-unification, Task 5 / Requirements 2, 4.4–4.9, 4.11).
 *
 * The admin approval-gating surfaces that still receive a legacy
 * `ApplicantWithProfile[]` roster (keyed by the Applicant record `id`, carrying
 * the durable `cognitoSub`) must derive approval status from the Approval_SSOT
 * (`ApplicationResponse.submissionStatus`), NOT the legacy
 * `ApplicantYearProfile.submissionStatus` roster (Req 4.11). This hook is the
 * single bridge: it consumes the Canonical_Approval_Reader's admin/director
 * list mode (`useApprovalAccessList`, one `listResponses(competition)` read) and
 * joins each SSOT row onto the Applicant record `id`.
 *
 * The join key: the SSOT list is keyed by the applicant's durable `personId`;
 * each admin row carries a `cognitoSub`, so this hook resolves
 * `personId = personIdForCognitoSub(cognitoSub)` (byte-identical to the server
 * contract, via `callerIdentity`) once per row. A row without a `cognitoSub`
 * cannot be joined and resolves to the not-approved default (mirrors "absent
 * `ApplicationResponse` ⇒ not approved", Req 2.3).
 *
 * Loading / error discipline (Req 4.10, 2.7, 2.8):
 *   - `ready` is true only once BOTH the SSOT list read AND the personId join
 *     have completed successfully. While not ready, every lookup resolves to the
 *     not-approved default and callers MUST withhold approval filtering/counting
 *     and present a loading state.
 *   - IF the SSOT list read fails, `error` is set and no previously loaded
 *     status is presented (the underlying list mode clears its map on failure) —
 *     callers withhold filter/count and present an error state, never a stale
 *     Approved value.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useApprovalAccessList,
  type UseApprovalAccessListResult,
} from './useApprovalAccess';
import type { SubmissionStatus } from '@/lib/application-form/client';
import { personIdForCognitoSub } from '@/lib/application-form/client/callerIdentity';

/** The minimal admin-roster identity a row must expose to be joined to the SSOT. */
export interface ApplicantIdentityRow {
  /** The Applicant record id the caller keys its rows by. */
  id: string;
  /** The durable Cognito subject the SSOT `personId` is derived from. */
  cognitoSub?: string | null;
}

export interface UseApplicantApprovalStatusResult {
  /**
   * The Approval_SSOT `submissionStatus` for an admin row, keyed by the
   * Applicant record `id`, or `null` when the row cannot be joined / has no
   * `ApplicationResponse` in the list read (Req 2.3). Always `null` until
   * `ready` (Req 2.7).
   */
  getStatus(applicantId: string): SubmissionStatus | null;
  /** True iff the row's SSOT status is `APPROVED` (Req 2.2). `false` until ready. */
  isApproved(applicantId: string): boolean;
  /** True iff the row's SSOT status is `SUBMITTED` (the approval queue, Req 4.5). `false` until ready. */
  isSubmitted(applicantId: string): boolean;
  /** True once BOTH the list read and the personId join have completed (Req 2.7). */
  ready: boolean;
  /** True while the list read or the personId join is in flight (Req 2.7). */
  loading: boolean;
  /** Distinct error indication — set when the SSOT list read fails (Req 2.8). */
  error: string | null;
  /** The underlying admin/director list-mode result (e.g. for `refetch`). */
  list: UseApprovalAccessListResult;
}

/**
 * Join a legacy admin roster to the Approval_SSOT list read.
 *
 * @param applicants The admin rows to resolve approval status for (id + cognitoSub).
 * @param competitionId The competition to read approval status for; when omitted
 *   the underlying list mode resolves the active-season operative competition.
 */
export function useApplicantApprovalStatus(
  applicants: ApplicantIdentityRow[],
  competitionId?: string,
): UseApplicantApprovalStatusResult {
  const list = useApprovalAccessList(competitionId);

  // A stable, content-based key so the personId join only re-runs when the set
  // of (id, cognitoSub) pairs actually changes — not on every array identity
  // change from a parent re-render.
  const rosterKey = useMemo(
    () => applicants.map((a) => `${a.id}:${a.cognitoSub ?? ''}`).join('|'),
    [applicants],
  );

  // Keep the latest rows available to the effect without widening its deps.
  const rowsRef = useRef(applicants);
  rowsRef.current = applicants;

  const [personIdByApplicantId, setPersonIdByApplicantId] = useState<Map<string, string>>(
    new Map(),
  );
  const [joinReady, setJoinReady] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setJoinReady(false);

    (async () => {
      const rows = rowsRef.current;
      const next = new Map<string, string>();
      await Promise.all(
        rows.map(async (row) => {
          if (!row.cognitoSub) return; // unjoinable ⇒ not-approved default (Req 2.3)
          try {
            const personId = await personIdForCognitoSub(row.cognitoSub);
            next.set(row.id, personId);
          } catch {
            // A derivation failure leaves the row unjoined (not-approved default);
            // it never grants approval (Req 8.6).
          }
        }),
      );
      if (cancelled) return;
      setPersonIdByApplicantId(next);
      setJoinReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [rosterKey]);

  return useMemo<UseApplicantApprovalStatusResult>(() => {
    // `ready` requires the SSOT read to have succeeded AND the join to be done.
    const ready = list.ready && joinReady;

    const getStatus = (applicantId: string): SubmissionStatus | null => {
      if (!ready) return null; // withhold until ready (Req 2.7, 4.10)
      const personId = personIdByApplicantId.get(applicantId);
      if (!personId) return null; // unjoinable / absent ⇒ not approved (Req 2.3)
      return list.getStatus(personId);
    };

    return {
      getStatus,
      isApproved: (applicantId) => getStatus(applicantId) === 'APPROVED',
      isSubmitted: (applicantId) => getStatus(applicantId) === 'SUBMITTED',
      ready,
      // Loading while the list read is in flight or the join has not resolved,
      // unless the list read has already errored (error takes precedence).
      loading: list.error === null && (list.loading || !joinReady),
      error: list.error,
      list,
    };
  }, [list, joinReady, personIdByApplicantId]);
}
